import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';

interface SectionSummaryRecord {
  id: string;
  key: string;
  summary: string;
  updatedAt: string;
}

interface GeneratedOptions {
  concise: string;
  detailed: string;
  engaging: string;
}

interface TileSummaryBlockProps {
  /** Unique key identifying this section (e.g., "legacy-ball-overview") */
  sectionKey: string;
  /** Whether the user has Organizer+ access (shows edit/generate controls) */
  isOrganizer: boolean;
  /** Optional context string sent to the AI for generation */
  contextForAI?: string;
}

/**
 * Universal summary block for dashboard tiles and page sections.
 * Fetches from the server-stored SectionSummary API.
 * Organizers can edit directly or generate with AI (3 options: concise, detailed, engaging).
 */
export function TileSummaryBlock({ sectionKey, isOrganizer, contextForAI }: TileSummaryBlockProps) {
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // AI generation state
  const [generating, setGenerating] = useState(false);
  const [options, setOptions] = useState<GeneratedOptions | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient<SectionSummaryRecord | null>(`/section-summaries/${sectionKey}`)
      .then((data) => {
        if (!cancelled && data) {
          setSummary(data.summary);
        }
      })
      .catch(() => {
        // Graceful degradation — no summary loaded
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [sectionKey]);

  const handleSave = async () => {
    const val = draft.trim();
    if (!val) return;
    setSaving(true);
    try {
      const saved = await apiClient<SectionSummaryRecord>('/section-summaries', {
        method: 'PUT',
        body: { key: sectionKey, summary: val },
      });
      setSummary(saved.summary);
      setEditing(false);
      setOptions(null);
    } catch (err) {
      console.warn('[TileSummaryBlock] Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!contextForAI) return;
    setGenerating(true);
    setOptions(null);
    try {
      const result = await apiClient<GeneratedOptions>('/section-summaries/generate', {
        method: 'POST',
        body: { key: sectionKey, context: contextForAI },
      });
      setOptions(result);
    } catch (err) {
      console.warn('[TileSummaryBlock] Generation failed:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handlePickOption = (text: string) => {
    setDraft(text);
    setEditing(true);
    setOptions(null);
  };

  if (loading) return null;
  if (!summary && !isOrganizer) return null;

  return (
    <div style={styles.container}>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={styles.textarea}
            placeholder="Enter a summary for this section..."
            maxLength={500}
          />
          <div style={styles.actions}>
            <button onClick={() => { setEditing(false); setOptions(null); }} style={styles.cancelBtn}>Cancel</button>
            <button onClick={handleSave} disabled={saving || !draft.trim()} style={styles.saveBtn}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : options ? (
        <div>
          <p style={styles.pickLabel}>Choose a summary option:</p>
          {(['concise', 'detailed', 'engaging'] as const).map((variant) => (
            <button
              key={variant}
              onClick={() => handlePickOption(options[variant])}
              style={styles.optionBtn}
              aria-label={`Use ${variant} summary`}
            >
              <span style={styles.optionLabel}>{variant}</span>
              <span style={styles.optionText}>{options[variant]}</span>
            </button>
          ))}
          <button onClick={() => setOptions(null)} style={styles.cancelBtn}>Dismiss</button>
        </div>
      ) : (
        <div style={styles.displayRow}>
          <p style={{ ...styles.summaryText, fontStyle: summary ? 'normal' : 'italic' }}>
            {summary || 'No summary set.'}
          </p>
          {isOrganizer && (
            <div style={styles.controlGroup}>
              {contextForAI && (
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  style={styles.generateBtn}
                  title="Generate with AI"
                  aria-label="Generate summary with AI"
                >
                  {generating ? '⏳' : '✨'}
                </button>
              )}
              <button
                onClick={() => { setDraft(summary); setEditing(true); }}
                style={styles.editBtn}
                title="Edit summary"
                aria-label="Edit summary"
              >
                ✏️
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginBottom: '0.75rem',
    padding: '0.6rem 0.75rem',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },
  textarea: {
    width: '100%',
    minHeight: '60px',
    fontSize: '0.82rem',
    padding: '6px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  actions: {
    display: 'flex',
    gap: '6px',
    justifyContent: 'flex-end',
    marginTop: '4px',
  },
  cancelBtn: {
    background: 'none',
    border: 'none',
    fontSize: '0.75rem',
    color: '#666',
    cursor: 'pointer',
    padding: '3px 8px',
  },
  saveBtn: {
    padding: '3px 10px',
    fontSize: '0.75rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  displayRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
  },
  summaryText: {
    margin: 0,
    fontSize: '0.82rem',
    color: '#333',
    lineHeight: 1.4,
    flex: 1,
  },
  controlGroup: {
    display: 'flex',
    gap: '2px',
    flexShrink: 0,
  },
  editBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.8rem',
    padding: '2px 4px',
  },
  generateBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.8rem',
    padding: '2px 4px',
  },
  pickLabel: {
    margin: '0 0 6px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#444',
  },
  optionBtn: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    marginBottom: '6px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  },
  optionLabel: {
    display: 'block',
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: '#1a6b3c',
    marginBottom: '2px',
    letterSpacing: '0.5px',
  },
  optionText: {
    display: 'block',
    fontSize: '0.8rem',
    color: '#333',
    lineHeight: 1.35,
  },
};
